import { currencyFormatter } from '@/helpers/currencyFormatter';
import { Box, Button, Modal } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState, type CSSProperties } from 'react';

import { CustomerPaymentLink } from 'types';

import { DeactivatePaymentLinkModal } from './DeactivatePaymentLinkModal';
import { PaymentLinkActionMenu } from './PaymentLinkActionMenu';

interface ActivePaymentLinkExistsModalProps {
  isOpen: boolean;
  onClose: () => void;
  links: CustomerPaymentLink[];
}

const WARNING_BANNER_STYLE: CSSProperties = {
  backgroundColor: 'var(--color-brand-danger-50)',
  borderRadius: 'var(--size-border-radius-md)',
};
const INFO_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
  gap: 'var(--size-spacing-lg)',
  alignItems: 'start',
};
function truncateLink(link: string): string {
  if (!link) return '';
  try {
    const url = new URL(link);
    const display = `${url.host}${url.pathname}`;
    return display.length > 24 ? `${display.slice(0, 24)}…` : display;
  } catch {
    return link.length > 24 ? `${link.slice(0, 24)}…` : link;
  }
}

function formatShortDate(date: Date | string): string {
  return DateTime.fromJSDate(new Date(date)).toFormat('LLLL d, yyyy');
}

function formatShortDateTime(date: Date | string): string {
  return DateTime.fromJSDate(new Date(date)).toFormat("LLLL d 'at' h:mm a");
}

function formatCurrency(value: string | undefined): string {
  if (!value) return '$0.00';
  return currencyFormatter.format(parseFloat(value));
}

export function ActivePaymentLinkExistsModal({ isOpen, onClose, links }: ActivePaymentLinkExistsModalProps) {
  const activeLink = useMemo(
    () => (links ?? []).find((l) => new Date(l.expiresAt) > new Date() && !l.linkSuccessfullyUsedAt),
    [links],
  );

  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const openDeactivateModal = useCallback(() => setIsDeactivateModalOpen(true), []);
  const closeDeactivateModal = useCallback(() => {
    setIsDeactivateModalOpen(false);
    onClose();
  }, [onClose]);

  if (!activeLink) return null;

  return (
    <>
      <Modal ariaLabelledBy="active-payment-link-title" isOpen={isOpen} onDismiss={onClose} maxWidth="680px">
        <Modal.Header id="active-payment-link-title" title="Send a direct payment link" onDismiss={onClose} />
        <Modal.Body childGap="lg">
          <Box padding="lg" childGap="sm" style={WARNING_BANNER_STYLE}>
            <Box as="p" fontSize="md" fontWeight="bold" color="danger">
              This account already has an active payment link
            </Box>
            <Box as="p" fontSize="sm">
              Only one active payment link is allowed at a time. You must wait for the active link to expire, or
              deactivate it before sending a new one.
            </Box>
          </Box>

          <Box style={INFO_GRID_STYLE}>
            <Box childGap="2xs">
              <Box as="p" fontSize="sm" color="body-secondary">
                Payment link
              </Box>
              <Box as="p" fontSize="sm" fontWeight="bold" title={activeLink.link}>
                {truncateLink(activeLink.link)}
              </Box>
            </Box>
            <Box childGap="2xs">
              <Box as="p" fontSize="sm" color="body-secondary">
                Date created
              </Box>
              <Box as="p" fontSize="sm" fontWeight="bold">
                {formatShortDate(activeLink.sentAt)}
              </Box>
            </Box>
            <Box childGap="2xs">
              <Box as="p" fontSize="sm" color="body-secondary">
                Last opened
              </Box>
              <Box as="p" fontSize="sm" fontWeight="bold">
                {activeLink.lastOpenedAt ? formatShortDateTime(activeLink.lastOpenedAt) : 'Not opened'}
              </Box>
            </Box>
            <Box childGap="2xs">
              <Box as="p" fontSize="sm" color="body-secondary">
                Min allowed payment
              </Box>
              <Box as="p" fontSize="sm" fontWeight="bold">
                {formatCurrency(String(activeLink.minAllowedPayment ?? 0))}
              </Box>
            </Box>
            <Box childGap="2xs">
              <Box as="p" fontSize="sm" color="body-secondary">
                Actions
              </Box>
              <PaymentLinkActionMenu link={activeLink.link} isActive onDeactivate={openDeactivateModal} />
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Box direction="row" justifyContent="flex-end" width="100">
            <Button variant="primary" size="md" type="button" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Modal.Footer>
      </Modal>

      <DeactivatePaymentLinkModal isOpen={isDeactivateModalOpen} onClose={closeDeactivateModal} />
    </>
  );
}
